import type { ReactNode } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import FileUpload from '@/components/FileUpload';
import Badge from '@/design_system/Badge';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import IconStore from '@/icons/Store.svg';
import IconStoreRepair from '@/icons/StoreRepair.svg';
import { ArticleConditionForm } from '@/models/article';
import { Medium } from '@/models/medium';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';

export const RepairedPhotoUpload = ({
  articleConditionForms,
}: {
  articleConditionForms: ArticleConditionForm[];
}) => {
  const { t } = useLingui();
  const { article, errors, view } = useArticleContext();

  const editableArticleConditionForm = articleConditionForms.find(
    (articleConditionForm) => !articleConditionForm.completedAt
  );

  const editable = view.qualityControl.editable && editableArticleConditionForm;

  const conditionPhotos: (Medium & { customIcon?: ReactNode })[] =
    articleConditionForms?.flatMap((articleConditionForm) =>
      (articleConditionForm?.media ?? []).map((medium) => ({
        ...medium,
        customIcon: articleConditionForm?.store ? (
          <Badge color="secondary" size="large" iconOnly>
            <IconStore />
          </Badge>
        ) : articleConditionForm?.workshop ? (
          <Badge color="secondary" size="large" iconOnly>
            <IconStoreRepair />
          </Badge>
        ) : undefined,
      }))
    ) ?? [];

  return (
    <FileUpload
      ariaLabel={t({
        id: 'article.form.quality-control.repaired-photo.title',
        message: 'Pictures of the repaired item',
      })}
      label={
        <>
          <Trans id="article.form.quality-control.repaired-photo.title">
            Pictures of the repaired item
          </Trans>
          {editable && !view.qualityControl.photos.required && <OptionalLabel />}
        </>
      }
      media={conditionPhotos}
      uploadData={{
        type: 'repaired-photo',
        articleId: article?.id,
        articleConditionFormId: editableArticleConditionForm?.id,
      }}
      style={{ flex: '1' }}
      size="xlarge"
      deleteWithApi
      variant="grid"
      withQrCode
      type="photo"
      disabled={!editable}
      error={
        editable && errors.qualityControl?.repairedPhoto
          ? t({
              id: 'article.form.quality-control.repaired-photo.error',
              message: 'A photo of the repaired item is required',
            })
          : undefined
      }
    />
  );
};
